import {
  PERMIT_GAS_SERVICE_CHANGES,
  PERMIT_SEPTIC_OR_SEWER,
  SURVEY_SECTION_FIELDS,
  type PermitSection,
} from '../../types';

/** The permit section's field names, in form order. */
export const PERMIT_FIELD_NAMES: readonly (keyof PermitSection)[] = SURVEY_SECTION_FIELDS.permit;

/** A house never has more alarms than this; anything above is a typo, not a count. */
export const MAX_PERMIT_ALARM_COUNT = 50;

/** Same cap the server applies to every section string. */
export const MAX_PERMIT_NOTE_LENGTH = 2000;

const isCount = (v: unknown): boolean =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= MAX_PERMIT_ALARM_COUNT;

const oneOf =
  (allowed: readonly string[]) =>
  (v: unknown): boolean =>
    typeof v === 'string' && allowed.includes(v);

const RULES: Record<keyof PermitSection, { ok: (v: unknown) => boolean; expected: string }> = {
  smokeAlarmCount: { ok: isCount, expected: `a whole number from 0 to ${MAX_PERMIT_ALARM_COUNT}` },
  coAlarmCount: { ok: isCount, expected: `a whole number from 0 to ${MAX_PERMIT_ALARM_COUNT}` },
  sprinklersOrMonitoredAlarm: { ok: (v) => typeof v === 'boolean', expected: 'yes or no' },
  treesRemoved: { ok: (v) => typeof v === 'boolean', expected: 'yes or no' },
  wellOnParcel: { ok: (v) => typeof v === 'boolean', expected: 'yes or no' },
  openPlanningPermitsOrHoa: { ok: (v) => typeof v === 'boolean', expected: 'yes or no' },
  openPlanningPermitsOrHoaNote: {
    ok: (v) => typeof v === 'string' && v.length <= MAX_PERMIT_NOTE_LENGTH,
    expected: `text up to ${MAX_PERMIT_NOTE_LENGTH} characters`,
  },
  gasServiceChange: {
    ok: oneOf(PERMIT_GAS_SERVICE_CHANGES),
    expected: `one of ${PERMIT_GAS_SERVICE_CHANGES.join(', ')}`,
  },
  septicOrSewer: {
    ok: oneOf(PERMIT_SEPTIC_OR_SEWER),
    expected: `one of ${PERMIT_SEPTIC_OR_SEWER.join(', ')}`,
  },
};

/**
 * Why a value cannot be stored in a permit field, or null when it can.
 * Clearing a field (null) is decided by the caller, not here: a null is never
 * a valid stored value, so it is refused like any other wrong type.
 */
export function permitFieldError(field: string, value: unknown): string | null {
  if (!Object.prototype.hasOwnProperty.call(RULES, field)) {
    return `${field} is not a permit field`;
  }
  const rule = RULES[field as keyof PermitSection];
  return rule.ok(value) ? null : `${field} must be ${rule.expected}`;
}
